import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../services/api.js';

const initialState = {
  users: [],
  loading: true,
  error: '',
  success: '',
  isEditModalOpen: false,
  editingUser: null,
};

export const fetchUsers = createAsyncThunk(
  'manageUsers/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/users');
      return response.data || [];
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to fetch users.';
      return rejectWithValue({ message });
    }
  }
);

export const updateUser = createAsyncThunk(
  'manageUsers/updateUser',
  async ({ userId, updatedData }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/users/${userId}`, updatedData);
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to update user.';
      return rejectWithValue({ message });
    }
  }
);

export const deleteUser = createAsyncThunk(
  'manageUsers/deleteUser',
  async ({ userId }, { rejectWithValue }) => {
    try {
      await api.delete(`/users/${userId}`);
      return { userId };
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to delete user.';
      return rejectWithValue({ message });
    }
  }
);

const manageUsersSlice = createSlice({
  name: 'manageUsers',
  initialState,
  reducers: {
    setEditModalOpen(state, action) {
      state.isEditModalOpen = action.payload;
    },
    setEditingUser(state, action) {
      state.editingUser = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    clearError(state) {
      state.error = '';
    },
    setSuccess(state, action) {
      state.success = action.payload;
    },
    clearSuccess(state) {
      state.success = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.users = action.payload;
        state.loading = false;
        state.error = '';
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch users.';
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.users = state.users.map((user) =>
          user._id === action.payload._id ? action.payload : user
        );
        state.isEditModalOpen = false;
        state.editingUser = null;
        state.error = '';
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to update user.';
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((user) => user._id !== action.payload.userId);
        state.error = '';
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to delete user.';
      });
  },
});

export const {
  setEditModalOpen,
  setEditingUser,
  setError,
  clearError,
  setSuccess,
  clearSuccess,
} = manageUsersSlice.actions;

export const selectManageUsers = (state) => state.manageUsers;

export default manageUsersSlice.reducer;
